/**
 * CheTuo Web — Settings page (门店设置).
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { listMyShops, updateShopName } from '../../api/shop';
import { useAuth } from '../../hooks/useAuth';
import { tokenStorage, ApiError } from '../../api/client';
import { Store, User, Pencil, Check, X } from 'lucide-react';

const trialStateLabels: Record<string, { label: string; variant: 'primary' | 'success' | 'warning' | 'danger' }> = {
  trial: { label: '试用中', variant: 'primary' },
  active: { label: '已激活', variant: 'success' },
  readonly: { label: '只读模式', variant: 'warning' },
  expired: { label: '已过期', variant: 'danger' },
};

export function SettingsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout } = useAuth();
  const { data: shops } = useQuery({
    queryKey: ['shops'],
    queryFn: listMyShops,
  });

  const currentShopId = tokenStorage.getShopId();
  const shop = shops?.find((s) => s.id === currentShopId) ?? shops?.[0];

  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (shop) setNameInput(shop.name);
  }, [shop]);

  const renameMutation = useMutation({
    mutationFn: () => updateShopName(nameInput.trim()),
    onSuccess: () => {
      setEditingName(false);
      setError('');
      queryClient.invalidateQueries({ queryKey: ['shops'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '修改失败'),
  });

  const handleLogout = () => {
    logout();
    navigate('/shop-admin/login');
  };

  const trialInfo = shop ? trialStateLabels[shop.trialState] : null;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#1E293B]">门店设置</h1>
        <p className="mt-1 text-sm text-[#64748B]">门店信息与账号管理</p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-[#FEF2F2] px-4 py-3 text-sm text-[#DC2626]">{error}</div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* 用户信息 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              用户信息
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between border-b border-[#F1F5F9] pb-3">
                <span className="text-sm text-[#64748B]">昵称</span>
                <span className="text-sm font-medium text-[#1E293B]">
                  {user?.displayName ?? '未设置'}
                </span>
              </div>
              <div className="flex justify-between border-b border-[#F1F5F9] pb-3">
                <span className="text-sm text-[#64748B]">手机号</span>
                <span className="text-sm font-medium text-[#1E293B]">
                  {user?.phone ?? '未绑定'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-[#64748B]">角色</span>
                <Badge variant="primary">门店管理员</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 门店信息 */}
        {shop && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="h-5 w-5" />
                门店信息
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-3">
                  <span className="text-sm text-[#64748B]">门店名称</span>
                  {editingName ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        className="h-8 w-40"
                      />
                      <button
                        onClick={() => renameMutation.mutate()}
                        disabled={renameMutation.isPending || !nameInput.trim()}
                        className="rounded-lg p-1 text-[#16A34A] hover:bg-[#F0FDF4]"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingName(false);
                          setNameInput(shop.name);
                        }}
                        className="rounded-lg p-1 text-[#94A3B8] hover:bg-[#F1F5F9]"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[#1E293B]">{shop.name}</span>
                      <button
                        onClick={() => setEditingName(true)}
                        className="rounded-lg p-1 text-[#2563EB] hover:bg-[#EFF6FF]"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex justify-between border-b border-[#F1F5F9] pb-3">
                  <span className="text-sm text-[#64748B]">状态</span>
                  {trialInfo && (
                    <Badge variant={trialInfo.variant}>
                      {trialInfo.label}
                      {shop.trialState === 'trial' && ` (剩 ${shop.remainingTrialDays} 天)`}
                    </Badge>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-[#64748B]">到期日</span>
                  <span className="text-sm font-medium text-[#1E293B]">
                    {new Date(shop.trialEndsAt).toLocaleDateString('zh-CN')}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* 退出登录 */}
      <Card className="mt-4">
        <CardContent className="flex items-center justify-between p-6">
          <div>
            <div className="font-medium text-[#1E293B]">退出当前账号</div>
            <div className="text-sm text-[#64748B]">退出后需要重新登录才能访问后台</div>
          </div>
          <Button variant="danger" onClick={handleLogout}>
            退出登录
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
